<script module lang="ts">
  type Mode = {
    name: string;
    icon: any;
    desc: string;
    path: string;
    beta?: boolean;
  };

  export const modes = [
    {
      name: 'Mini',
      icon: MiniIcon,
      desc: 'Make words along rows and columns. Clear the board in 18 swaps.',
      path: 'mini',
      beta: false,
    },
    {
      name: 'Daily',
      icon: DailyIcon,
      desc: 'Mini too easy? Now use 20 swaps to clear a 6x6 board.',
      path: 'daily',
    },
    {
      name: 'Classic',
      icon: Star,
      desc: 'Make 4+ letter words to score points, but be careful not to run out of swaps.',
      path: 'classic',
    },
    ...(false
      ? [
          {
            name: 'Twist',
            icon: Rotate,
            desc: 'Unscramble the grid by twisting and swapping 2x2 groups of tiles',
            path: 'twist',
          },
        ]
      : []),
  ];
</script>

<script lang="ts">
  import PlayIcon from '~icons/mdi/play';
  import Rotate from '~icons/mdi/cached';
  import DailyIcon from '~/icons/DailyIcon.svelte';
  import MiniIcon from '~/icons/MiniIcon.svelte';
  import AwardIcon from '~/icons/AwardIcon.svelte';
  import Star from '~/icons/Star.svelte';
  import ActionButton from './ActionButton.svelte';
  import ArchiveIcon from '~/icons/ArchiveIcon.svelte';
  import { getTodayUTC, tileDate } from '~/utils/dates';
  import Premium from '~/icons/Premium.svelte';
  import { isPremium } from '~/lib/queries.svelte';
  import HelpCircle from '~/icons/HelpCircle.svelte';
  import { goto } from '$app/navigation';

  type Props = {
    mode: Mode;
  };
  const props: Props = $props();
  const premium = isPremium();
  const today = getTodayUTC();
  const disabled = $derived(props.mode.beta && !premium.data);

  const handleClick = (href: string) => {
    goto(href);
  };
</script>

<div
  onclick={(e) => handleClick(props.mode.path)}
  onkeydown={(e) => e.key === 'Enter' && handleClick(props.mode.path)}
  class:beta={props.mode.beta}
  role="button"
  tabindex={-1}
  class="mode game-gradient {props.mode.path}"
>
  <div class="icon-name">
    <div class="icon">
      <props.mode.icon selected size={48} />
    </div>
    <h3>
      {props.mode.name}
    </h3>
    <div class="tutorial-button">
      <ActionButton
        {disabled}
        href="{props.mode.path}?rules=b"
        outlink={false}
        iconOnly
      >
        <HelpCircle />
      </ActionButton>
    </div>
  </div>
  <p class="description">
    {props.mode.desc}
  </p>
  <div class="controls">
    {#if props.mode.path === 'classic'}
      <ActionButton
        {disabled}
        class="leaderboard"
        href="/classic?leaderboard=b"
        outlink={false}
      >
        <AwardIcon size="1.5em" />
        Leaderboard
      </ActionButton>
    {:else if premium.data}
      <ActionButton
        {disabled}
        href="{props.mode.path}?archive=b"
        outlink={false}
      >
        <ArchiveIcon size="1.5em" />
        Archive
      </ActionButton>
    {:else}
      <ActionButton {disabled} href="?premium=t#archive" outlink={false}>
        <Premium size="1.5em" />
        Archive
      </ActionButton>
    {/if}
    <ActionButton href="/{props.mode.path}" outlink={false}>
      {#if props.mode.path === 'classic'}
        Play
      {:else}
        {tileDate(today.toISOString())}
      {/if}
      <PlayIcon />
    </ActionButton>
  </div>
</div>

<style lang="scss">
  @use '~/theme/theme';
  @use '~/theme/include-media' as *;

  .mode {
    cursor: pointer;
    position: relative;
    overflow: clip;
    transition: transform 0.25s ease;
    text-decoration: none;
    color: currentColor;
    display: grid;
    grid-auto-flow: row;
    height: 100%;
    width: 100%;
    padding: 1rem;
    gap: 0.5rem;
    border-radius: 10px;
    border: 2px solid currentColor;
    background-blend-mode: lighten;
    &:hover,
    &:active {
      transform: scale(1.05);
    }
    h3 {
      margin: 0;
    }
    & :global(.action) {
      display: flex;
      align-items: center;
    }
    & :global(.yellow) {
      stroke-width: 1px;
      stroke: currentColor;
    }
    .controls {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: flex-end;
      gap: 0.5rem;

      @include media('>tablet') {
        margin-block-start: 1rem;
        justify-self: end;
      }
    }
  }
  .icon-name {
    width: 90%;
    justify-self: center;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    grid-auto-flow: column;
    align-items: center;
    justify-content: center;
    gap: 2rem;
  }
  .icon {
    justify-self: flex-start;
  }
  .tutorial-button {
    display: flex;
    justify-content: flex-end;
    :global(svg) {
      width: 32px;
      height: 32px;
    }
  }

  .description {
    margin: 0.25em 0;
    font-size: 1rem;
  }

  :global(html.dark-mode .mode .action),
  :global(.mode .action) {
    background-color: transparent;
    &:hover {
      background-color: #fff6;
    }
    &:active {
      background-color: #fff9;
    }
  }

  :global(html.dark-mode .tutorial-button .action),
  :global(.tutorial-button .action) {
    border: none;
  }

  :global(.leaderboard svg) {
    overflow: visible;
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 3px;
  }
</style>
